import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ClearwaterTransaction, ExternalTransaction } from '@helios/common-api'
import { transactionsNotApplicableMessage } from '@helios/generic-components'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  CreateTransactionsModalService,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  ClearwaterTransactionsPanelHotkeysType,
  HotkeyDestroyFn,
  HotkeysService,
} from '@helios/hotkeys'
import {
  EventType,
  HeliosMessageType,
  SessionBasedEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  SessionCacheStatus,
  getSelectAllRowsEnabled,
  selectActiveSession,
  selectCwTransactionsSessionCacheStatus,
  selectExtTranIdsFilter,
  selectExternalTransactions,
  selectFocusedAssetId,
  selectFocusedCWTransaction,
  selectFocusedCWTransactions,
  selectFocusedLegacyOrigIds,
  selectFocusedLegacyTranIds,
  selectIsEnfusionSession,
  selectIsSwitchingSession,
  selectTransactionCurrencyCodeFilter,
  selectTransactionSecurityIdFilter,
  setFocusedSecurities,
  setFocusedSubSleeveAccountId,
  setSelectAllRowsEnabled,
  setTransactions,
  storeSessionCwTxDataAvailableTime,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators'

import { UserPreferencesService } from '@helios/user-preferences'
import {
  ClearwaterTransactionsGridComponent,
  CwTxSelectionEvent,
} from './components/clearwater-transactions-grid/clearwater-transactions-grid.component'
import { CwTxSummaryResponse } from './models/cw-tx-summary.model'
import { ClearwaterTransactionsTaskService } from './services/clearwater-transactions-task.service'

// TODO: remove this in PVAL-14397
export type CwTranSSEEventPayload = string[] | { rowIds: string[] }

@Component({
  selector: 'hs-clearwater-transactions-panel',
  templateUrl: './clearwater-transactions-panel.component.html',
  styleUrls: ['./clearwater-transactions-panel.component.scss'],
})
export class ClearwaterTransactionsPanelComponent
  extends BasePanelComponent
  implements OnInit, OnDestroy
{
  /**
   * @internal for testing
   */
  @ViewChild('cwTransactionsGrid')
  readonly clearwaterTransactionsGridComponent!: ClearwaterTransactionsGridComponent

  /**
   * needed for automated testing, so we know when the cache has loaded.
   * also passed down to the grid
   */
  @HostBinding('attr.data-cy-cache-status')
  sessionCacheStatus = SessionCacheStatus.UNKNOWN

  lastProcessedSessionId?: string
  lastProcessedAccountIds?: number[]

  /**
   * True when the active session is Enfusion-sourced (`kind === 'ENFN'`). The grid is
   * then never created and a static message card is shown instead of waiting on a
   * session cache that will never become ready for these accounts.
   */
  notApplicable = false
  readonly notApplicableMessage = transactionsNotApplicableMessage

  /** @internal */
  externalTransactionSelection: ExternalTransaction[] = []
  //filter down the panel when a transaction break is 'focused' (e.g. double-click the break or "Show Details")
  focusedTranId$ = this.store.select(selectFocusedCWTransaction)
  focusedTranIds$ = this.store.select(selectFocusedCWTransactions)
  focusedLegacyTranIds$ = this.store.select(selectFocusedLegacyTranIds)
  focusedLegacyOrigIds$ = this.store.select(selectFocusedLegacyOrigIds)
  focusedAssetId?: string
  isSelectAllRowsEnabled = this.store.select(getSelectAllRowsEnabled)
  readonly securityIdFilter = this.store.select(
    selectTransactionSecurityIdFilter,
  )
  readonly currencyFilter = this.store.select(
    selectTransactionCurrencyCodeFilter,
  )

  readonly filteredExtTranIds = this.store
    .select(selectExtTranIdsFilter)
    .pipe(map(x => x.extTranIds))

  readonly updatedTransactions = this.messagingService
    .on<CwTranSSEEventPayload, SessionBasedEvent<CwTranSSEEventPayload>>(
      EventType.MESSAGE,
      [
        HeliosMessageType.CWTransactionUpdate,
        HeliosMessageType.CWTransactionDelete,
      ],
    )
    .pipe(
      map(e => {
        // TODO: remove this in PVAL-14397
        if (Array.isArray(e.payload)) {
          return e.payload
        }

        const tranIds = e.payload.rowIds.map(id => id.split('/')[0].trim())
        return tranIds
      }),
    )

  readonly createdTransactions = this.messagingService
    .on<CwTranSSEEventPayload, SessionBasedEvent<CwTranSSEEventPayload>>(
      EventType.MESSAGE,
      [HeliosMessageType.CWTransactionCreate],
    )
    .pipe(
      map(e => {
        // TODO: remove this in PVAL-14397
        if (Array.isArray(e.payload)) {
          return e.payload
        }
        const tranIds = e.payload.rowIds.map(id => id.split('/')[0].trim())
        return tranIds
      }),
    )

  private selectedCwTrans: ClearwaterTransaction[] = []

  private readonly _destroyed$ = new Subject<void>() // cleanup object for observables
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []
  sessionCacheStartTime: number | undefined
  selectedRowIds: string[] = []
  summaryDataResponse: CwTxSummaryResponse = {}

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly preferences: UserPreferencesService,
    private readonly messagingService: SessionMessagingService,
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly taskService: ClearwaterTransactionsTaskService,
    private readonly hotkeysService: HotkeysService<ClearwaterTransactionsPanelHotkeysType>,
  ) {
    super(container)
  }

  ngOnInit() {
    this.store
      .select(selectIsEnfusionSession)
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(isEnfusionSession => (this.notApplicable = isEnfusionSession))

    this.store
      .select(selectExternalTransactions)
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(
        externalTransactionSelection =>
          (this.externalTransactionSelection = externalTransactionSelection),
      )

    this.store
      .select(selectFocusedAssetId)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(result => {
        this.focusedAssetId = result
      })

    this.store
      .select(selectCwTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status => {
        this.sessionCacheStatus = status
        this.handleSessionCacheStatusUpdate(status)
      })

    this.store
      .select(selectIsSwitchingSession)
      .pipe(distinctUntilChanged(), takeUntil(this._destroyed$))
      .subscribe(isSwitchingSession => {
        if (isSwitchingSession) {
          this.sessionCacheStartTime = Date.now()
        }
      })

    this.registerHotkeys()
  }

  ngOnDestroy() {
    this.unregisterHotkeys()
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /** @internal */
  createCashEntryTx() {
    this.createTransactionsModalService.toggleCashEntryModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createTradeEntryTx() {
    this.createTransactionsModalService.toggleTradeEntryModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createFxSpotTradeTx() {
    this.createTransactionsModalService.toggleFxSpotTradeModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createFxForwardTx() {
    this.createTransactionsModalService.toggleFxForwardModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createDirectTransferTx() {
    this.createTransactionsModalService.toggleDirectTransferModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createWireTransferTx() {
    this.createTransactionsModalService.toggleWireTransferModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  /** @internal */
  createSecurityTransferTx() {
    this.createTransactionsModalService.toggleSecurityTransferModal({
      modalMode: TransactionModalMode.CreateCWTran,
    })
  }

  onTransactionsSelected({ cwTrans, newSelection }: CwTxSelectionEvent) {
    if (newSelection) {
      const securityIds = cwTrans.map(t => t.secId).filter(id => id) as number[]
      const selectedTranAccountId = cwTrans
        .map(t => t.accountId)
        .filter(id => id)
      this.store.dispatch(setFocusedSecurities({ securityIds }))
      this.store.dispatch(
        setFocusedSubSleeveAccountId({
          subSleeveAccountId: selectedTranAccountId[0],
        }),
      )
    }

    this.selectedCwTrans = cwTrans
    this.store.dispatch(setTransactions({ transactions: cwTrans }))
  }

  getRegisteredHotkeyCount(): number {
    return this.hotkeyDestroyStack.length
  }

  private registerHotkeys() {
    this.hotkeyDestroyStack.push(
      this.hotkeysService.bind('delete-selected-transaction', () => {
        this.taskService.delete(this.selectedCwTrans)
      }),
      this.hotkeysService.bind('find-matched-ext-trans', () => {
        this.taskService.findMatchedTransaction(this.selectedCwTrans)
      }),
      this.hotkeysService.bind('select-all-cw-trans', () => {
        this.handleSelectAllRowsSelection()
      }),
    )
  }

  handleSelectAllRowsSelection() {
    this.store.dispatch(setSelectAllRowsEnabled({ selectAllRowsEnabled: true }))
    this.clearwaterTransactionsGridComponent.selectAllExcludedTranRows.clear()
  }

  onSelectAllTransactionState(state: boolean) {
    this.store.dispatch(
      setSelectAllRowsEnabled({ selectAllRowsEnabled: state }),
    )
  }

  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  handleSessionCacheStatusUpdate(status: SessionCacheStatus): void {
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        if (
          session?.id == null ||
          this.lastProcessedSessionId === session?.id
        ) {
          return
        }

        this.lastProcessedSessionId = session.id
        this.lastProcessedAccountIds = session.loadedAccountIds

        if (
          status == SessionCacheStatus.PARTIAL ||
          status == SessionCacheStatus.DONE
        ) {
          this.store.dispatch(
            storeSessionCwTxDataAvailableTime({
              sessionId: session.id,
              cwTxDataAvailableTime: Date.now(),
            }),
          )
        }
      })
  }
}
